/**
 * BreastHealthCoach.jsx — iter654
 * Section 5e "🤝 Breast Health Coach AI" — dynamic monthly coaching.
 *
 * Sections A-F + condition coaching + weekly task checkboxes with 30-day streak.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Heart, Sparkles, RefreshCw, Loader2, Trophy, AlertCircle, MinusCircle,
  TrendingUp, TrendingDown, Calendar, Flag, CheckSquare, Square, Flame,
  Award, ShieldAlert,
} from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";

const TX = {
  bg: {
    title: "🤝 Breast Health Coach AI",
    subtitle:
      "Динамичен месечен coaching спрямо текущото състояние на тъканната среда. Образователно — не диагноза, не предсказване, не обещание за изход.",
    refresh: "Обнови coaching",
    enrich: "✨ AI coaching съобщение",
    enriching: "Генерира…",
    cached: "от кеш",
    forceRefresh: "Нов AI анализ",
    setBaseline: "Запиши като baseline",
    baselineSet: "Baseline записан ✓",
    timeoutMsg: "AI отговорът отнема повече от обичайното. Опитайте отново.",
    noAi: "Натиснете AI coaching съобщение за персонализиран motivational преглед.",
    empty: "Зареждане…",
    sectionA: "A. Месечно coaching ревю",
    sectionB: "B. Прогрес спрямо baseline",
    sectionC: "C. Топ-3 приоритета",
    sectionD: "D. План за тази седмица",
    sectionE: "E. Breast Tissue Optimization Score",
    sectionF: "F. Следваща ревизия",
    streak: "Текуща серия",
    longestStreak: "Най-дълга",
    band: { excellent: "Отличен", good: "Добър", fair: "Среден", needs_focus: "Нуждае се от фокус", unknown: "—" },
    completed: "Готово!",
    markDone: "Отбележи като направено",
    conditionCoaching: "Coaching при наличните състояния",
    next: "Следваща",
    weeks: "седмици",
    baseline: "Baseline",
    delta: "Δ",
    current: "Сега",
  },
  en: {
    title: "🤝 Breast Health Coach AI",
    subtitle:
      "Dynamic monthly coaching against your current tissue-environment state. Educational — not a diagnosis, prediction or outcome promise.",
    refresh: "Refresh coaching",
    enrich: "✨ AI coaching message",
    enriching: "Generating…",
    cached: "cached",
    forceRefresh: "New AI analysis",
    setBaseline: "Save as baseline",
    baselineSet: "Baseline saved ✓",
    timeoutMsg: "AI is taking longer than usual. Please try again.",
    noAi: "Click the AI coaching message button for a motivational summary.",
    empty: "Loading…",
    sectionA: "A. Monthly coaching review",
    sectionB: "B. Progress since baseline",
    sectionC: "C. Top-3 priorities",
    sectionD: "D. This week's plan",
    sectionE: "E. Breast Tissue Optimization Score",
    sectionF: "F. Next reassessment",
    streak: "Current streak",
    longestStreak: "Longest",
    band: { excellent: "Excellent", good: "Good", fair: "Fair", needs_focus: "Needs focus", unknown: "—" },
    completed: "Done!",
    markDone: "Mark as done",
    conditionCoaching: "Condition-specific coaching",
    next: "Next",
    weeks: "weeks",
    baseline: "Baseline",
    delta: "Δ",
    current: "Now",
  },
  es: {
    title: "🤝 Coach IA de Salud Mamaria",
    subtitle:
      "Coaching dinámico mensual sobre el estado actual del entorno tisular. Educativo — no es diagnóstico, predicción o promesa de resultado.",
    refresh: "Actualizar coaching",
    enrich: "✨ Mensaje IA",
    enriching: "Generando…",
    cached: "en caché",
    forceRefresh: "Nuevo análisis IA",
    setBaseline: "Guardar como baseline",
    baselineSet: "Baseline guardado ✓",
    timeoutMsg: "La IA está tardando más. Intente de nuevo.",
    noAi: "Pulse Mensaje IA para un resumen motivacional.",
    empty: "Cargando…",
    sectionA: "A. Revisión mensual",
    sectionB: "B. Progreso desde baseline",
    sectionC: "C. Top-3 prioridades",
    sectionD: "D. Plan de esta semana",
    sectionE: "E. Breast Tissue Optimization Score",
    sectionF: "F. Próxima evaluación",
    streak: "Racha actual",
    longestStreak: "Más larga",
    band: { excellent: "Excelente", good: "Bueno", fair: "Regular", needs_focus: "Necesita enfoque", unknown: "—" },
    completed: "Hecho!",
    markDone: "Marcar como hecho",
    conditionCoaching: "Coaching para condiciones presentes",
    next: "Próxima",
    weeks: "semanas",
    baseline: "Baseline",
    delta: "Δ",
    current: "Ahora",
  },
};

const BAND_COLORS = {
  excellent:   "text-emerald-500",
  good:        "text-teal-500",
  fair:        "text-amber-500",
  needs_focus: "text-orange-500",
  unknown:     "text-slate-400",
};

const DIRECTION_ICON = {
  improvement:   { Icon: TrendingUp,   cls: "text-emerald-500" },
  deterioration: { Icon: TrendingDown, cls: "text-red-500" },
  unchanged:     { Icon: MinusCircle,  cls: "text-amber-500" },
  unknown:       { Icon: MinusCircle,  cls: "text-slate-400" },
};

export default function BreastHealthCoach({ lang = "bg" }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const t = TX[lang] || TX.bg;

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [enrich, setEnrich] = useState(null);
  const [enrichLoading, setEnrichLoading] = useState(false);
  const [baselineMsg, setBaselineMsg] = useState(null);
  const [streak, setStreak] = useState(null);
  const [completedTasks, setCompletedTasks] = useState(new Set());

  const computeReport = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await axios.post(`${API}/bhi/coach/monthly`, { lang });
      setReport(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [API, lang]);

  const fetchStreak = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/bhi/coach/streak?days=30`);
      setStreak(data);
      const today = new Date().toISOString().slice(0, 10);
      // Mark today's logged tasks as visually checked.
      // (We don't have per-task day map yet; rely on active_days for streak visual.)
      const todays = data.active_days?.includes(today) ? new Set() : new Set();
      // (Future: fetch per-task entries; for now we keep local optimistic state.)
      if (todays.size) setCompletedTasks(todays);
    } catch { /* optional */ }
  }, [API]);

  useEffect(() => {
    computeReport();
    fetchStreak();
  }, [computeReport, fetchStreak]);

  const runEnrich = async (force = false) => {
    if (!report) return;
    setEnrichLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/bhi/coach/enrich`,
        { report, lang, force_refresh: force },
        { timeout: 90000 },
      );
      setEnrich(data);
    } catch (e) {
      const status = e?.response?.status;
      const isTimeout = status === 502 || status === 504 || e?.code === "ECONNABORTED";
      setEnrich({
        error: isTimeout ? t.timeoutMsg : (e?.response?.data?.detail || e.message),
      });
    } finally {
      setEnrichLoading(false);
    }
  };

  const setBaseline = async () => {
    try {
      await axios.post(`${API}/bhi/coach/baseline`, {});
      setBaselineMsg(t.baselineSet);
      setTimeout(() => setBaselineMsg(null), 2400);
      computeReport();
    } catch (e) {
      setBaselineMsg(e?.response?.data?.detail || e.message);
      setTimeout(() => setBaselineMsg(null), 3000);
    }
  };

  const toggleTask = async (task) => {
    const next = new Set(completedTasks);
    const wasCompleted = next.has(task.id);
    if (wasCompleted) next.delete(task.id); else next.add(task.id);
    setCompletedTasks(next);
    try {
      if (!wasCompleted) {
        await axios.post(`${API}/bhi/coach/log-action`, {
          task_id: task.id,
          action_label: task.task,
        });
        fetchStreak();
      }
    } catch {
      /* revert silently */
      const reverted = new Set(completedTasks);
      setCompletedTasks(reverted);
    }
  };

  const cardCls = `rounded-2xl border ${
    isDark ? "bg-[hsl(222,47%,11%)] border-[hsl(223,47%,18%)]" : "bg-white border-gray-200"
  }`;

  const sectionA = report?.section_a_monthly_summary || {};
  const sectionE = report?.section_e_optimization_score || {};
  const bandColor = BAND_COLORS[sectionE.band] || "text-slate-400";

  // 30-day streak map: render last 30 days as dots.
  const streakDots = useMemo(() => {
    if (!streak) return [];
    const days = [];
    const today = new Date();
    for (let i = 29; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const k = d.toISOString().slice(0, 10);
      days.push({ day: k, active: streak.active_days?.includes(k) });
    }
    return days;
  }, [streak]);

  return (
    <div className="space-y-5" data-testid="bhc-coach">
      {/* Header */}
      <div className={`${cardCls} p-5`}>
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-rose-500 flex items-center justify-center shrink-0">
            <Heart className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-extrabold bg-gradient-to-r from-amber-500 to-rose-500 bg-clip-text text-transparent">
              {t.title}
            </h3>
            <p className={`text-xs mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>{t.subtitle}</p>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            onClick={computeReport}
            disabled={loading}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300 hover:opacity-90 flex items-center gap-1.5"
            data-testid="bhc-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> {t.refresh}
          </button>
          <button
            onClick={() => runEnrich(false)}
            disabled={enrichLoading || !report}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-amber-500 to-rose-500 text-white hover:opacity-90 flex items-center gap-1.5 disabled:opacity-50"
            data-testid="bhc-enrich-btn"
          >
            {enrichLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {enrichLoading ? t.enriching : t.enrich}
          </button>
          <button
            onClick={setBaseline}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300 hover:opacity-90 flex items-center gap-1.5"
            data-testid="bhc-baseline-btn"
          >
            <Flag className="w-3.5 h-3.5" /> {t.setBaseline}
          </button>
          {baselineMsg && <span className="text-xs text-emerald-600 dark:text-emerald-400">{baselineMsg}</span>}
        </div>
      </div>

      {/* Disclaimer */}
      {report?.disclaimer && (
        <div
          className={`p-3 rounded-xl border-l-4 flex items-start gap-2 ${
            isDark ? "bg-amber-500/10 border-amber-500" : "bg-amber-50 border-amber-500"
          }`}
          data-testid="bhc-disclaimer"
        >
          <ShieldAlert className={`w-4 h-4 shrink-0 mt-0.5 ${isDark ? "text-amber-300" : "text-amber-600"}`} />
          <p className={`text-[11px] leading-relaxed ${isDark ? "text-amber-200" : "text-amber-700"}`}>
            {report.disclaimer}
          </p>
        </div>
      )}

      {loading && (
        <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin mx-auto opacity-50" /><p className="mt-2 text-xs opacity-70">{t.empty}</p></div>
      )}
      {err && <div className="p-3 rounded-lg bg-red-500/10 text-red-500 text-sm" data-testid="bhc-error">{err}</div>}

      {report && !loading && (
        <>
          {/* SECTION E + F + STREAK — header row */}
          <div className="grid md:grid-cols-3 gap-3">
            <div className={`${cardCls} p-4`} data-testid="bhc-section-e">
              <div className="text-[10px] uppercase opacity-60 font-bold">{t.sectionE}</div>
              <div className={`text-4xl font-extrabold mt-1 ${bandColor}`}>
                {sectionE.score ?? "—"}
              </div>
              <div className={`text-xs font-bold mt-0.5 ${bandColor}`}>
                {t.band[sectionE.band] || sectionE.band}
              </div>
            </div>

            <div className={`${cardCls} p-4`} data-testid="bhc-section-f">
              <div className="flex items-center gap-1.5 text-[10px] uppercase opacity-60 font-bold">
                <Calendar className="w-3 h-3" /> {t.sectionF}
              </div>
              <div className="text-xl font-extrabold mt-1">{report.section_f_next_reassessment?.next_review_at}</div>
              <div className="text-xs opacity-70 mt-0.5">
                {t.next}: {report.section_f_next_reassessment?.weeks_from_today} {t.weeks}
              </div>
            </div>

            <div className={`${cardCls} p-4`} data-testid="bhc-streak">
              <div className="flex items-center gap-1.5 text-[10px] uppercase opacity-60 font-bold">
                <Flame className="w-3 h-3 text-orange-500" /> {t.streak}
              </div>
              <div className="text-2xl font-extrabold mt-1">
                {streak?.current_streak ?? 0}
                <span className="text-xs opacity-60 ml-1">/ {t.longestStreak} {streak?.longest_streak ?? 0}</span>
              </div>
              <div className="flex gap-0.5 mt-2 flex-wrap">
                {streakDots.map((d) => (
                  <div
                    key={d.day}
                    title={d.day}
                    className={`w-2 h-2 rounded-sm ${d.active ? "bg-orange-500" : "bg-black/10 dark:bg-white/10"}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* SECTION A — Wins / Concerns / Steady */}
          <div className={`${cardCls} p-5`} data-testid="bhc-section-a">
            <h4 className="text-sm font-extrabold uppercase tracking-wide opacity-70 mb-3">{t.sectionA}</h4>
            <div className="grid md:grid-cols-3 gap-3">
              <SubGroup
                Icon={Trophy} label={sectionA.wins_label} items={sectionA.wins} kind="win"
              />
              <SubGroup
                Icon={AlertCircle} label={sectionA.concerns_label} items={sectionA.concerns} kind="concern"
              />
              <SubGroup
                Icon={MinusCircle} label={sectionA.steady_label} items={sectionA.steady} kind="steady"
              />
            </div>
          </div>

          {/* SECTION B — Baseline progress */}
          <div className={`${cardCls} p-5`} data-testid="bhc-section-b">
            <h4 className="text-sm font-extrabold uppercase tracking-wide opacity-70 mb-3">{t.sectionB}</h4>
            <div className="grid sm:grid-cols-2 gap-2">
              {(report.section_b_progress_baseline || []).map((m) => {
                const { Icon, cls } = DIRECTION_ICON[m.direction] || DIRECTION_ICON.unknown;
                return (
                  <div
                    key={m.metric}
                    className={`p-2.5 rounded-lg border ${isDark ? "border-white/10" : "border-gray-100"} flex items-center gap-2`}
                  >
                    <Icon className={`w-4 h-4 ${cls} shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold truncate">{m.label}</div>
                      <div className="text-[10px] opacity-60 flex gap-2">
                        <span>{t.baseline}: {m.baseline != null ? Math.round(m.baseline) : "—"}</span>
                        <span>{t.current}: {m.current != null ? Math.round(m.current) : "—"}</span>
                        {m.delta != null && <span className={cls}>{t.delta}{m.delta > 0 ? "+" : ""}{Math.round(m.delta)}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* SECTION C — Top-3 priorities */}
          <div className={`${cardCls} p-5`} data-testid="bhc-section-c">
            <h4 className="text-sm font-extrabold uppercase tracking-wide opacity-70 mb-3">{t.sectionC}</h4>
            <div className="space-y-2">
              {(report.section_c_top3_priorities || []).map((p) => (
                <motion.div
                  key={p.metric}
                  layout
                  className={`p-3 rounded-xl border-l-4 ${
                    p.direction === "deterioration" ? "border-red-500 bg-red-50 dark:bg-red-500/10"
                    : p.direction === "unchanged"  ? "border-amber-500 bg-amber-50 dark:bg-amber-500/10"
                    : "border-emerald-500 bg-emerald-50 dark:bg-emerald-500/10"
                  }`}
                  data-testid={`bhc-priority-${p.metric}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold opacity-50">#{p.priority}</span>
                    <span className="text-sm font-bold">{p.label}</span>
                  </div>
                  {p.hint && <div className="text-xs opacity-80">{p.hint}</div>}
                </motion.div>
              ))}
            </div>
          </div>

          {/* SECTION D — Weekly plan with checkboxes */}
          <div className={`${cardCls} p-5`} data-testid="bhc-section-d">
            <h4 className="text-sm font-extrabold uppercase tracking-wide opacity-70 mb-3">{t.sectionD}</h4>
            <ul className="space-y-2">
              {(report.section_d_weekly_plan || []).map((task) => {
                const done = completedTasks.has(task.id);
                return (
                  <li key={task.id} data-testid={`bhc-task-${task.metric}`}>
                    <button
                      onClick={() => toggleTask(task)}
                      className={`w-full text-left px-3 py-2 rounded-lg border flex items-start gap-2 transition-all ${
                        done
                          ? `${isDark ? "bg-emerald-500/10 border-emerald-500/40" : "bg-emerald-50 border-emerald-200"} line-through opacity-70`
                          : `${isDark ? "border-white/10 hover:bg-white/5" : "border-gray-200 hover:bg-gray-50"}`
                      }`}
                    >
                      {done ? (
                        <CheckSquare className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                      ) : (
                        <Square className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <div className="text-sm">{task.task}</div>
                        <div className="text-[10px] opacity-50 mt-0.5">{task.category}</div>
                      </div>
                      {done && <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-bold">{t.completed}</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Condition coaching */}
          {(report.condition_coaching || []).length > 0 && (
            <div className={`${cardCls} p-5`} data-testid="bhc-condition-coaching">
              <h4 className="text-sm font-extrabold uppercase tracking-wide opacity-70 mb-3">{t.conditionCoaching}</h4>
              <div className="grid md:grid-cols-2 gap-3">
                {report.condition_coaching.map((cc) => (
                  <div
                    key={cc.condition_id}
                    className={`p-3 rounded-xl border ${isDark ? "border-white/10" : "border-gray-100"}`}
                    data-testid={`bhc-cond-${cc.condition_id}`}
                  >
                    <div className="flex items-center gap-1.5 mb-2">
                      <Award className="w-4 h-4 text-rose-500" />
                      <span className="font-semibold text-sm">{cc.condition_id}</span>
                    </div>
                    <ul className="space-y-0.5 text-xs">
                      {cc.tasks.map((it, i) => (
                        <li key={i} className="flex items-start gap-1.5"><span className="text-rose-500">▸</span><span>{it}</span></li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AI narrative */}
          <div className={`${cardCls} p-5`} data-testid="bhc-ai-section">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5 text-rose-500" />
              <h4 className="text-base font-bold">{t.enrich.replace("✨ ", "")}</h4>
              {enrich?.cached && (
                <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                  {t.cached}
                </span>
              )}
            </div>
            {enrich?.narrative ? (
              <div className={`whitespace-pre-wrap text-sm leading-relaxed ${isDark ? "text-gray-200" : "text-gray-800"}`} data-testid="bhc-ai-text">
                {enrich.narrative}
              </div>
            ) : enrich?.error ? (
              <div className="text-sm text-red-500" data-testid="bhc-ai-error">{enrich.error}</div>
            ) : (
              <div className="text-sm opacity-60 italic">{t.noAi}</div>
            )}
            {enrich?.narrative && (
              <button
                onClick={() => runEnrich(true)}
                disabled={enrichLoading}
                className="mt-3 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300 hover:opacity-90 flex items-center gap-1.5"
                data-testid="bhc-ai-refresh-btn"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${enrichLoading ? "animate-spin" : ""}`} /> {t.forceRefresh}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function SubGroup({ Icon, label, items, kind }) {
  const ring = {
    win:     "ring-emerald-500/20",
    concern: "ring-red-500/20",
    steady:  "ring-amber-500/20",
  }[kind] || "ring-slate-500/10";
  const accent = {
    win:     "text-emerald-500",
    concern: "text-red-500",
    steady:  "text-amber-500",
  }[kind];
  return (
    <div className={`p-3 rounded-xl ring-1 ${ring}`}>
      <div className="flex items-center gap-1.5 mb-2">
        <Icon className={`w-4 h-4 ${accent}`} />
        <span className="font-semibold text-xs">{label}</span>
        <span className="ml-auto text-[10px] opacity-50">{(items || []).length}</span>
      </div>
      {(items || []).length === 0 ? (
        <div className="text-xs opacity-50 italic">—</div>
      ) : (
        <ul className="space-y-0.5 text-xs">
          {items.slice(0, 5).map((it, i) => (
            <li key={i} className="flex items-center justify-between gap-2">
              <span className="truncate">{it.label}</span>
              {it.delta != null && (
                <span className={`text-[10px] font-bold ${accent}`}>
                  {it.delta > 0 ? "+" : ""}{Math.round(it.delta)}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
